import { useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useUpdateRelease, useCancelRelease, useSmartLinkStats, releaseUpdateBody } from "../../../app/hooks/useRelease.js";
import { useTasks } from "../../../app/hooks/useTasks.js";
import { useTaskPeople } from "../../tasks/taskPeople.js";
import { TaskListRow } from "../../tasks/TaskListRow.js";
import { taskRowsClassName, taskSubLine, sortTasksForList } from "../../tasks/taskRow.js";
import { TaskDrawer } from "../../tasks/TaskDrawer.js";
import { ConfirmDialog } from "../../shared/ConfirmDialog.js";
import { SectionRail } from "../sectionRail.js";
import { daysToRelease, formatShortDate } from "../releaseFormat.js";
import styles from "../section.module.scss";
import own from "./OverviewSection.module.scss";

// Overview (shot-09) — the calm read on where the rollout stands: created→release progress bar, four
// KPI tiles, a Needs Attention list, the Release overview form (Spotify album ID + TikTok timestamp + short
// blurb), and the Danger Zone (cancel = soft delete). The save is a full PUT (If-Match) built from the current
// overview + the edited fields — `releaseUpdateBody` carries every field, so this save cannot wipe one it does
// not show. Cancel confirms through the branded destructive dialog. Money-free (not money:*).

export function OverviewSection({ releaseId }: { releaseId: string }) {
  const navigate = useNavigate();
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const update = useUpdateRelease(releaseId);
  const cancel = useCancelRelease(releaseId);
  // The same rollup read step 04 renders two sections over — the tile was a hardwired em-dash with a
  // "tracking soon" sub-line while the number was already being served.
  const stats = useSmartLinkStats(releaseId);
  // Needs attention reads the FULL task list (the shared `useTasks`), not the command-center's lighter
  // `toDo.items` (which carries no assignees) — so the rows can render faces + status like the To-do section
  // and the dashboard card. Same read the 03 To-do tab uses, so it is warm when the reader lands there.
  const tasksQ = useTasks({ releaseId });
  const people = useTaskPeople(tasksQ.data?.people ?? undefined);

  const overview = q.data?.data?.overview;
  const version = q.data?.version;
  const toDo = q.data?.data?.toDo;
  const assets = q.data?.data?.assets?.progress;
  const smartLink = q.data?.data?.smartLink;
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const [name, setName] = useState<string | null>(null);
  const [blurb, setBlurb] = useState<string | null>(null);
  const [tiktok, setTiktok] = useState<string | null>(null);
  const [album, setAlbum] = useState<string | null>(null);
  const [label, setLabel] = useState<string | null>(null);
  const [albumError, setAlbumError] = useState<string | null>(null);
  // Explicit ids rather than the wrapping-<label> the two fields below use: this one carries a hint AND an
  // error, and both must be `aria-describedby` the input. Nested inside the label they would instead become
  // part of its accessible NAME — the field would announce as "Spotify album ID We'll pull in the track…".
  const albumFieldId = useId();
  const albumHintId = `${albumFieldId}-hint`;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (q.isLoading || !overview) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading overview…" />
      </div>
    );
  }

  // Controlled inputs default to the server value until the user edits them (null = untouched).
  const nameValue = name ?? overview.title ?? "";
  const blurbValue = blurb ?? overview.shortBlurb ?? "";
  const tiktokValue = tiktok ?? overview.tiktokTimestamp ?? "";
  // The server stores the PARSED id, so what comes back is a bare 22-char id even though the field accepts a
  // link or a URI. That is the field telling the truth about what was understood.
  const albumValue = album ?? overview.spotifyAlbumId ?? "";
  const labelValue = label ?? overview.labelName ?? "";
  const dirty =
    (name !== null && name !== (overview.title ?? "")) ||
    (blurb !== null && blurb !== (overview.shortBlurb ?? "")) ||
    (tiktok !== null && tiktok !== (overview.tiktokTimestamp ?? "")) ||
    (label !== null && label !== (overview.labelName ?? "")) ||
    (album !== null && album !== (overview.spotifyAlbumId ?? ""));

  const days = daysToRelease(overview.releaseDate);
  // Triage order (overdue first), top five — the same sort the dashboard's To-do card uses.
  const attention = sortTasksForList(
    (tasksQ.data?.items ?? []).filter((t) => t.status !== "done" && t.status !== "approved"),
  ).slice(0, 5);
  const checklistPct = (toDo?.total ?? 0) > 0 ? Math.round(((toDo?.doneCount ?? 0) / (toDo!.total as number)) * 100) : 0;

  function saveOverview() {
    if (version === undefined) return;
    setAlbumError(null);
    // No client-side format check on the album reference. `SpotifyAlbumRef.Parse` on the server is the
    // authority, and the copy that used to live on the Details section disagreed with it in both directions —
    // it rejected the `spotify:album:{id}` URI form the server accepts, and accepted any string merely
    // CONTAINING "/album/". A 400 surfaces through `albumError` below.
    update.mutate(
      {
        version,
        body: releaseUpdateBody(overview!, {
          // Blank falls back to the same "Untitled release" the hero shows, so the title is never empty.
          title: nameValue.trim() || "Untitled release",
          spotifyAlbumId: albumValue.trim() || null,
          shortBlurb: blurbValue.trim() || null,
          tiktokTimestamp: tiktokValue.trim() || null,
          // Empty ⇒ NULL, never the empty string: null is what makes the hub banner's "Self-released" a
          // fallback the band has not answered, rather than a value it stored. Clearing the field has to get
          // back to that state or the fallback becomes unreachable once anyone types in the box.
          labelName: labelValue.trim() || null,
        }),
      },
      {
        onSuccess: () => { setName(null); setBlurb(null); setTiktok(null); setAlbum(null); setLabel(null); },
        onError: (err) => setAlbumError(err.message),
      },
    );
  }

  function confirmCancel() {
    setCancelError(null);
    cancel.mutate(undefined, {
      onSuccess: () => navigate({ to: "/releases" }),
      onError: (err) => setCancelError(err.message),
    });
  }

  return (
    <div className={styles.section}>
      <SectionRail>
        {canWrite ? (
          <Button size="sm" onClick={saveOverview} disabled={!dirty} loading={update.isPending}>
            Save
          </Button>
        ) : null}
      </SectionRail>

      <div className={styles.kpiRow}>
        <Kpi label="Days to release" value={days === null ? "—" : String(Math.abs(days))} sub={formatShortDate(overview.releaseDate) || "No date"} />
        {/* The leak contract: an unavailable read renders "—", never a fabricated 0; a genuinely loaded 0 is
            honest and shown as 0. */}
        <Kpi
          label="Smart-link visits 30d"
          value={stats.isError || stats.data?.visits30d == null ? "—" : String(stats.data.visits30d)}
          sub={smartLink?.slug ? "public page" : "no link"}
        />
        <Kpi
          label="Assets in"
          value={assets ? `${assets.added ?? 0} / ${assets.total ?? 0}` : "—"}
          sub={assets ? `${Math.max(0, (assets.total ?? 0) - (assets.added ?? 0))} to go` : ""}
        />
        <Kpi label="Checklist done" value={`${toDo?.doneCount ?? 0} / ${toDo?.total ?? 0}`} sub={`${checklistPct}% complete`} />
      </div>

      <div className={styles.grid2}>
        <div className={`${styles.card} ${own.clip}`}>
          <div className={own.cardHead}>
            <h3 className={styles.cardTitle}>Needs attention</h3>
          </div>
          {attention.length === 0 ? (
            <EmptyState title="All clear" description="No open to-dos need attention right now." />
          ) : (
            // The shared task row — the same rendering as the dashboard To-do card and the 03 To-do section
            // (Ryan, ART-201: "match To-do"). No status pill here: this is a triage list, where overdue is
            // the one signal that matters.
            <ul className={taskRowsClassName(own.attentionRows)}>
              {attention.map((t) => (
                <TaskListRow
                  key={t.taskId}
                  task={t}
                  people={people}
                  sub={taskSubLine(t, { release: false })}
                  onOpen={() => t.taskId && setOpenTaskId(t.taskId)}
                />
              ))}
            </ul>
          )}
        </div>

        <div className={styles.card}>
          <div className={own.cardHead}>
            <h3 className={styles.cardTitle}>Release overview</h3>
          </div>
          <div className={own.fields}>
            <label className={own.field}>
              <span className={own.fieldLabel}>Name</span>
              <input
                className={own.input}
                placeholder="Untitled release"
                value={nameValue}
                maxLength={200}
                disabled={!canWrite}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            {/* The release's Spotify ALBUM — the one paste that turns into per-track Spotify ids, and so into
                every stat the release card shows. Songstats has no album resource, so this is the only
                authoritative way to know what is on a release. Saving a NEW reference resolves the track list
                in the same request. Sole editor: section 06 shows the resolved id read-only. */}
            <div className={own.field}>
              <label className={own.fieldLabel} htmlFor={albumFieldId}>
                Spotify album ID
              </label>
              <input
                id={albumFieldId}
                className={own.input}
                placeholder="Paste the album link, URI or id"
                value={albumValue}
                disabled={!canWrite}
                aria-describedby={albumHintId}
                aria-invalid={albumError ? true : undefined}
                onChange={(e) => { setAlbum(e.target.value); setAlbumError(null); }}
              />
              <span id={albumHintId} className={own.fieldHint} data-error={albumError ? true : undefined} role={albumError ? "alert" : undefined}>
                {albumError ?? "We'll pull in the track list so this release's stats are its own."}
              </span>
            </div>
            {/* The label / distributor. Its PLACEHOLDER is "Self-released" because that is exactly what the hub
                banner renders while this is empty — the field shows a band what the blank means rather than
                making them discover it on another screen. Left blank it stays NULL server-side; both
                codebases used to hardcode the string with no field behind it, so a signed band was told it had
                self-released and had nowhere to say otherwise (audit G-6). */}
            <label className={own.field}>
              <span className={own.fieldLabel}>Label</span>
              <input
                className={own.input}
                placeholder="Self-released"
                value={labelValue}
                disabled={!canWrite}
                onChange={(e) => setLabel(e.target.value)}
              />
            </label>
            <label className={own.field}>
              <span className={own.fieldLabel}>TikTok timestamp</span>
              <input
                className={own.tiktokInput}
                placeholder="0:46"
                value={tiktokValue}
                disabled={!canWrite}
                onChange={(e) => setTiktok(e.target.value)}
              />
            </label>
            <label className={own.field}>
              <span className={own.fieldLabel}>Release bio</span>
              <textarea
                className={own.textarea}
                placeholder="Write a short bio for this release…"
                value={blurbValue}
                disabled={!canWrite}
                onChange={(e) => setBlurb(e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>

      {canWrite && overview.status !== "cancelled" && (
        <div className={styles.danger}>
          <div className={styles.dangerText}>
            <span className={styles.dangerEyebrow}>Danger zone</span>
            <p className={styles.dangerBody}>
              Permanently cancel this release and everything attached — to-do, smart link, assets, timeline.
              Tagged ad campaigns stay; their release tag clears.
            </p>
          </div>
          <Button variant="accent" onClick={() => setConfirmOpen(true)}>
            Cancel release
          </Button>
        </div>
      )}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}

      {confirmOpen && (
        <ConfirmDialog
          title="Cancel this release?"
          description="This soft-deletes the release and everything attached. Tagged ad campaigns stay; their release tag clears."
          confirmLabel="Cancel release"
          error={cancelError}
          loading={cancel.isPending}
          onConfirm={confirmCancel}
          onCancel={() => {
            setConfirmOpen(false);
            setCancelError(null);
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiVal}>{value}</span>
      {sub && <span className={styles.kpiSub}>{sub}</span>}
    </div>
  );
}
